"use client";

import { memo, useEffect, useState, useMemo, useCallback } from "react";
import Image, { type StaticImageData } from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import {
  cn,
  FALLBACK_FEATURE_IMAGE,
  POPOVER_CONTENT_CLASSNAME,
} from "@/lib/utils";
import { CURRENT_LOCATION_LABEL } from "@/utils/constants/location";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "./ui/input";
import { Typography } from "./Typography";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { type GetTopDestinationSvcType } from "@/actions/destination.action";
import { DEFAULT_SVC_TYPE } from "./search-form/search.modules.type";
import { ChevronDown, MapPin, MapPinCheckInside, X } from "lucide-react";

const SVC_TYPE_LABEL: Record<
  GetTopDestinationSvcType,
  { singular: string; plural: string }
> = {
  all: { singular: "All", plural: "Alls" },
  hotel: { singular: "Hotel", plural: "Hotels" },
  rental: { singular: "Rental", plural: "Rentals" },
  tour_act: { singular: "Tour & Activitie", plural: "Tour & Activities" },
  spa_msg: { singular: "Spas & Massage", plural: "Spas & Massages" },
};

export type LocationComboboxOption = {
  id: string;
  label: string;
  image?: string | StaticImageData | null;
  country: string;
  totalItem?: number | null;
  svcType?: GetTopDestinationSvcType;
  kind?: "destination" | "hotel";
};

export type CustomComboboxOption = LocationComboboxOption;

export type LocationComboboxProps = {
  value?: string;
  onChange?: (value: string, option?: LocationComboboxOption) => void;
  onInputChange?: (value: string) => void;
  options?: LocationComboboxOption[];
  placeholder?: string;
  sectionTitle?: string;
  onUseCurrentLocation?: () => void;
  showCurrentLocation?: boolean;
  isCurrentLocationSelected?: boolean;
  isUsingCurrentLocation?: boolean;
  isLoadingOptions?: boolean;
  className?: string;
  isInline?: boolean;
  inlineExpanded?: boolean;
  onInlineExpandedChange?: (open: boolean) => void;
  inlineFloating?: boolean;
};

export type CustomComboboxProps = LocationComboboxProps;

const POPOVER_CONTENT_ID = "destination-select-popover";

const normalize = (text: string) =>
  text.toLowerCase().replace(/\s+/g, " ").trim();

const LocationCombobox = ({
  value,
  onChange,
  onInputChange,
  options = [],
  placeholder = "Where to?",
  sectionTitle = "Popular destinations",
  onUseCurrentLocation,
  showCurrentLocation = true,
  isCurrentLocationSelected = false,
  isUsingCurrentLocation = false,
  isLoadingOptions = false,
  className,
  isInline: isInlineProp,
  inlineExpanded = false,
  onInlineExpandedChange,
  inlineFloating = false,
}: LocationComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isInline =
    typeof isInlineProp === "boolean"
      ? isInlineProp
      : typeof onInlineExpandedChange === "function";
  const isOpen = isInline ? inlineExpanded : open;

  const selectedOption = useMemo(
    () => options.find((item) => item.id === value),
    [options, value],
  );

  const closeDropdown = useCallback(() => {
    if (isInline) {
      onInlineExpandedChange?.(false);
    } else {
      setOpen(false);
    }
  }, [isInline, onInlineExpandedChange]);

  const toggleDropdown = useCallback(() => {
    if (isInline) {
      onInlineExpandedChange?.(!isOpen);
    } else {
      setOpen(true);
    }
  }, [isInline, isOpen, onInlineExpandedChange]);

  useEffect(() => {
    if (isCurrentLocationSelected) {
      setSearchQuery(CURRENT_LOCATION_LABEL);
    } else if (!value) {
      setSearchQuery("");
    } else {
      setSearchQuery(selectedOption?.label ?? value);
    }
  }, [isCurrentLocationSelected, selectedOption, value]);

  const displayLabel = isCurrentLocationSelected
    ? CURRENT_LOCATION_LABEL
    : selectedOption?.label || value?.trim() || placeholder;

  const handleSelect = useCallback(
    (nextValue: string) => {
      const selected = options.find((item) => item.id === nextValue);
      setSearchQuery(selected?.label ?? nextValue);
      onChange?.(nextValue, selected);
      closeDropdown();
    },
    [closeDropdown, onChange, options],
  );

  const filteredOptions = useMemo(() => {
    if (isCurrentLocationSelected || !searchQuery) return options;

    const query = normalize(searchQuery);
    if (!query) return options;

    const words = query.split(" ");
    return options.filter((item) => {
      const target = normalize(`${item.label} ${item.country}`);
      return words.every((word) => target.includes(word));
    });
  }, [isCurrentLocationSelected, options, searchQuery]);

  const handleCurrentLocationClick = useCallback(() => {
    if (isUsingCurrentLocation) return;
    onUseCurrentLocation?.();
    closeDropdown();
  }, [closeDropdown, isUsingCurrentLocation, onUseCurrentLocation]);

  const handleClearClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setSearchQuery("");
      onInputChange?.("");
      onChange?.("");
    },
    [onChange, onInputChange],
  );

  const renderOptionItem = useCallback(
    (item: LocationComboboxOption) => {
      const isActive = item.id === value;
      const svcTypeKey = (item.svcType ??
        DEFAULT_SVC_TYPE) as GetTopDestinationSvcType;
      const svcLabel = SVC_TYPE_LABEL[svcTypeKey];

      return (
        <button
          key={item.id}
          type="button"
          onClick={() => handleSelect(item.id)}
          className={cn(
            "flex w-full items-center gap-2 rounded-xl p-1 text-left transition-colors",
            isActive ? "bg-white" : "hover:bg-subtle",
          )}
        >
          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md bg-subtle">
            {item.image ? (
              <Image
                src={item.image ?? FALLBACK_FEATURE_IMAGE}
                alt={item.label}
                fill
                className="object-cover"
                sizes="48px"
              />
            ) : null}
          </div>

          <div className="flex min-w-0 flex-col gap-1">
            <Typography variant="subtitle2" className="truncate">
              {item.label}{" "}
              {item.totalItem ? (
                <span className="text-caption font-normal">
                  ({item.totalItem}{" "}
                  {svcLabel?.[item.totalItem === 1 ? "singular" : "plural"] ??
                    "Hotels"}
                  )
                </span>
              ) : null}
            </Typography>

            {item.country ? (
              <Typography variant="body2" className="truncate text-caption">
                {item.country}
              </Typography>
            ) : null}
          </div>
        </button>
      );
    },
    [handleSelect, value],
  );

  const isSelected = Boolean(
    value || isCurrentLocationSelected || searchQuery.trim(),
  );

  const trigger = (
    <div
      role="button"
      aria-expanded={isOpen}
      aria-controls={POPOVER_CONTENT_ID}
      onClick={toggleDropdown}
      className={cn(
        "flex h-[46px] md:h-12 w-full items-center gap-2 px-4 rounded-full border bg-white transition-colors",
        "hover:border-primary focus-within:border-primary",
        isOpen && "border-primary",
        className,
      )}
    >
      <MapPin
        size={20}
        className={cn("shrink-0", isSelected ? "text-body" : "text-caption")}
      />

      <Input
        value={searchQuery}
        maxLength={250}
        placeholder={displayLabel}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          onInputChange?.(e.target.value);
          if (isInline) {
            onInlineExpandedChange?.(true);
          } else {
            setOpen(true);
          }
        }}
        className={cn(
          "flex-1 border-none bg-transparent p-0 shadow-none focus-visible:ring-0",
          isSelected
            ? "text-body font-semibold placeholder:text-body placeholder:font-semibold"
            : "text-caption font-normal placeholder:text-caption placeholder:font-normal",
        )}
      />

      {searchQuery.length > 0 && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={handleClearClick}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors hover:cursor-pointer hover:bg-subtle"
        >
          <X size={20} className="text-body" />
        </button>
      )}
      <ChevronDown
        size={20}
        className={cn(
          "shrink-0 transition-transform",
          isSelected ? "text-body" : "text-caption",
          isOpen && "rotate-180",
        )}
      />
    </div>
  );

  const content = (
    <div className="flex flex-col">
      {showCurrentLocation && (
        <button
          type="button"
          onClick={handleCurrentLocationClick}
          disabled={isUsingCurrentLocation}
          className={cn(
            "flex w-full rounded-t-3xl items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-70",
            isCurrentLocationSelected && "bg-subtle",
          )}
        >
          <div className="flex h-[34.32px] w-[34.32px] shrink-0 items-center justify-center rounded-full bg-primary-subtle">
            <MapPinCheckInside size={20} className="text-primary" />
          </div>

          <div className="min-w-0">
            <Typography variant="button-md">
              {isUsingCurrentLocation ? "Detecting location..." : "My Location"}
            </Typography>
            <Typography variant="body2" className="text-caption">
              {isUsingCurrentLocation
                ? "Getting your current city"
                : "Use current location"}
            </Typography>
          </div>
        </button>
      )}

      <div className={cn("px-4 pb-3 pt-5", showCurrentLocation && "border-t")}>
        <Typography variant="button-md">{sectionTitle}</Typography>
      </div>

      <div className="pb-2 pl-5 pr-2">
        <div className="tovtrip-scrollbar max-h-[min(295px,calc(100dvh-280px))] overflow-y-auto overscroll-contain md:max-h-[320px]">
          <div className="space-y-2 pr-2">
            {isLoadingOptions ? (
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`destination-skeleton-${index}`}
                  className="flex items-center gap-3 rounded-[18px] p-2"
                >
                  <Skeleton className="h-12 w-12 rounded-[14px]" />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-32 rounded-full" />
                    <Skeleton className="h-3 w-20 rounded-full" />
                  </div>
                </div>
              ))
            ) : filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-heading">
                No destinations found.
              </p>
            ) : (
              filteredOptions.map(renderOptionItem)
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isInline) {
    return (
      <div className={cn("relative", !inlineFloating && "space-y-2")}>
        {trigger}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
              className={cn(
                "rounded-2xl border bg-white p-0",
                inlineFloating
                  ? "absolute left-0 right-0 top-full z-30 mt-2"
                  : "",
              )}
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>

      <PopoverContent
        id={POPOVER_CONTENT_ID}
        side="bottom"
        align="start"
        avoidCollisions={false}
        sideOffset={8}
        collisionPadding={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(POPOVER_CONTENT_CLASSNAME, "md:w-[488px]")}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
};

export default memo(LocationCombobox);
